import React, { useEffect, useState } from 'react';
import {
  ChevronDown,
  Menu,
  X,
  Globe,
  Smartphone,
  Laptop,
  TrendingUp,
  Users,
  ShieldCheck,
  Code2,
  Award,
  Compass,
  ArrowRight,
  Briefcase,
  Zap,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

// --- MEGA MENU CONFIGURATION (Aligned to Site Color Palette: Brand Blue & Amber Gold) ---
const CORE_IT_SERVICES = [
  {
    name: 'Web Solutions',
    path: '/services/web-solutions',
    icon: Globe,
    desc: 'High-converting web platforms, SaaS portals & e-commerce',
    badge: 'Web & SaaS',
  },
  {
    name: 'Mobile Apps',
    path: '/services/mobile-apps',
    icon: Smartphone,
    desc: 'Native iOS & Android apps with offline caching & live sync',
    badge: 'iOS & Android',
  },
  {
    name: 'Desktop Software',
    path: '/services/desktop-software',
    icon: Laptop,
    desc: 'Centralized business operating ERPs & automated billing',
    badge: 'ERP & POS',
  },
  {
    name: 'Digital Marketing',
    path: '/digital-marketing',
    icon: TrendingUp,
    desc: 'Meta & Google Ads, technical SEO & qualified B2B leads',
    badge: 'Growth & Leads',
  },
];

const WORKFORCE_SERVICES = [
  {
    name: 'Manpower & Recruitment',
    path: '/manpower-recruitment',
    icon: Users,
    desc: 'Permanent, contract & executive staffing placement',
  },
  {
    name: 'HR & Payroll Outsourcing',
    path: '/hr-outsourcing',
    icon: ShieldCheck,
    desc: 'Statutory compliance (PF, ESI, TDS) & workforce ops',
  },
  {
    name: 'IT Solutions & Staffing',
    path: '/it-solutions',
    icon: Code2,
    desc: 'Dedicated developer squads & technical consulting',
  },
  {
    name: 'Skill Upgradation',
    path: '/skill-upgradation',
    icon: Award,
    desc: 'Corporate training programs & IT skill certifications',
  },
  {
    name: 'Travel & Tourism',
    path: '/travel-tourism',
    icon: Compass,
    desc: 'Corporate business travel, ticketing & visa assistance',
  },
];

const CAREER_ROLES = [
  {
    name: 'IT Roles & Engineering',
    path: '/it-careers',
    icon: Code2,
    isTech: true,
    desc: 'Full-Stack Developers, Mobile App Devs, QA & UI/UX Engineers',
  },
  {
    name: 'Non-IT Roles & Operations',
    path: '/non-it-careers',
    icon: Briefcase,
    isTech: false,
    desc: 'HR Recruiters, Business Development, Operations & Accounts',
  },
];

const Navbar = ({ navLinks, scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileCareerOpen, setMobileCareerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomeTop = (location.pathname === '/' || location.pathname === '/services/web-solutions') && !scrolled;
  const isMobileAppsTop = location.pathname === '/services/mobile-apps' && !scrolled;
  const isDesktopSoftwareTop = location.pathname === '/services/desktop-software' && !scrolled;
  const isDigitalMarketingTop = location.pathname === '/digital-marketing' && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const isTabActive = (link) => {
    const p = location.pathname;
    if (link.id === '/') return p === '/';
        if (link.id === '/about') return p === '/about' || p === '/global-eprc';
    if (link.id === '/contact') return p === '/contact';
    if (link.id === 'services' || link.name === 'Services') {
      return (
        p.startsWith('/services') ||
        p === '/digital-marketing' ||
        p === '/manpower-recruitment' ||
        p === '/hr-outsourcing' ||
        p === '/it-solutions' ||
        p === '/skill-upgradation' ||
        p === '/travel-tourism'
      );
    }
    if (link.id === 'career' || link.name === 'Career') {
      return p.startsWith('/career') || p === '/it-careers' || p === '/non-it-careers';
    }
    return p === link.id;
  };

  const handleNavClick = (link) => {
    setIsMenuOpen(false);
    if (link.isPage) {
      navigate(link.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => scrollToSection(link.id, false), 80);
      return;
    }
    scrollToSection(link.id, false);
  };

  const handleBookDemo = () => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      const demoSection = document.getElementById('demo-section');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    navigate('/');
    window.setTimeout(() => {
      const demoSection = document.getElementById('demo-section');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 120);
  };

  return (
    <nav
      className={`fixed inset-x-0 top-[28px] z-50 transition-all duration-300 ${
        isHomeTop
          ? 'border-b border-white/10 bg-transparent'
          : isMobileAppsTop || isDesktopSoftwareTop || isDigitalMarketingTop
          ? 'border-b border-slate-900/10 bg-transparent'
          : 'border-b border-slate-200/70 bg-white/85 shadow-[0_4px_25px_-5px_rgba(15,23,42,0.06)] backdrop-blur-xl'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-5 px-5 py-2.5 sm:px-8 lg:px-10 relative">
        {/* LOGO & BRAND */}
        <button
          type="button"
          onClick={() => handleNavClick({ id: '/', isPage: true })}
          className="group flex min-w-0 items-center gap-3 text-left focus:outline-none"
        >
          <span className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-2xl bg-white p-1.5 shadow-xs ring-1 ring-slate-900/5 transition-transform duration-200 group-hover:scale-105">
            <img src="/logo.png" alt="GlobalEPRC logo" className="h-full w-full object-contain" />
          </span>
          <span className="flex flex-col min-w-0">
            <span
              className={`block truncate text-[16px] font-black tracking-tight ${
                isHomeTop ? 'text-white' : 'text-slate-950'
              }`}
            >
              Global EPRC
            </span>
            <span
              className={`block max-w-[170px] xs:max-w-[220px] sm:max-w-[270px] truncate text-[9px] font-bold uppercase tracking-[0.14em] ${
                isHomeTop ? 'text-amber-300' : 'text-blue-600'
              }`}
            >
              Ethical People Resourcing Consultancy
            </span>
          </span>
        </button>

        {/* DESKTOP NAVIGATION LINKS */}
        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => {
            const active = isTabActive(link);
            const isServices = link.id === 'services' || link.name === 'Services';
            const isCareer = link.id === 'career' || link.name === 'Career';

            // Modern, sleek tab styling (Stripe / Linear modern aesthetic)
            let btnClass = '';
            if (active) {
              btnClass = isHomeTop
                ? 'text-white font-extrabold bg-white/15 backdrop-blur-md rounded-full shadow-xs ring-1 ring-white/20'
                : 'text-blue-700 font-extrabold bg-blue-50/80 rounded-full shadow-xs';
            } else {
              btnClass = isHomeTop
                ? 'text-white/85 hover:text-white hover:bg-white/10 font-bold rounded-full'
                : 'text-slate-600 hover:text-slate-950 hover:bg-slate-100/70 font-semibold rounded-full';
            }

            return (
              <div key={link.name} className={`group ${isServices ? 'static' : 'relative'}`}>
                <button
                  type="button"
                  onClick={() => handleNavClick(link)}
                  className={`relative flex items-center gap-1.5 px-3.5 py-1.5 text-[13.5px] transition-all duration-200 ${btnClass}`}
                >
                  {active && !isHomeTop && (
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                  )}
                  <span>{link.name}</span>
                  {link.subLinks && (
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180 opacity-70" />
                  )}
                </button>

                {/* 1. SERVICES MEGA MENU (Horizontally centered & safely bounded inside navbar) */}
                {isServices && (
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full w-[880px] max-w-[calc(100vw-32px)] pt-3 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 z-50">
                    <div className="overflow-hidden rounded-3xl border border-slate-200/90 bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.18)] ring-1 ring-black/5">
                      <div className="grid grid-cols-12 gap-6">
                        
                        {/* COLUMN 1: Core IT & Software (5 cols) */}
                        <div className="col-span-4 border-r border-slate-100 pr-4">
                          <div className="flex items-center gap-2 mb-3.5 pb-2 border-b border-slate-100">
                            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-100 text-blue-700 text-xs font-black">
                              IT
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-900">
                              Core IT Solutions
                            </span>
                          </div>
                          <div className="space-y-1">
                            {CORE_IT_SERVICES.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  type="button"
                                  key={item.name}
                                  onClick={() => handleNavClick({ id: item.path, isPage: true })}
                                  className="group/item flex items-start gap-2.5 w-full rounded-xl p-2 text-left transition hover:bg-slate-50"
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700 border border-blue-200/80 shadow-xs transition-colors group-hover/item:bg-blue-600 group-hover/item:text-white group-hover/item:border-blue-600">
                                    <Icon className="h-4 w-4" />
                                  </span>
                                  <span className="min-w-0">
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-xs font-black text-slate-900 group-hover/item:text-blue-700 transition-colors">
                                        {item.name}
                                      </span>
                                      <span className="rounded-md bg-blue-50 px-2 py-0.5 text-[8.5px] font-black uppercase tracking-wider text-blue-700 border border-blue-200/80 shrink-0">
                                        {item.badge}
                                      </span>
                                    </span>
                                    <span className="block text-[10px] text-slate-500 line-clamp-1 leading-snug mt-0.5">
                                      {item.desc}
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* COLUMN 2: Workforce & Business Consulting (4 cols) */}
                        <div className="col-span-4 border-r border-slate-100 pr-4">
                          <div className="flex items-center gap-2 mb-3.5 pb-2 border-b border-slate-100">
                            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-amber-100 text-amber-800 text-xs font-black border border-amber-200/80">
                              HR
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-900">
                              Workforce &amp; Consulting
                            </span>
                          </div>
                          <div className="space-y-1">
                            {WORKFORCE_SERVICES.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  type="button"
                                  key={item.name}
                                  onClick={() => handleNavClick({ id: item.path, isPage: true })}
                                  className="group/item flex items-start gap-2.5 w-full rounded-xl p-2 text-left transition hover:bg-slate-50"
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-800 border border-amber-200/80 shadow-xs transition-colors group-hover/item:bg-amber-500 group-hover/item:text-slate-950 group-hover/item:border-amber-500">
                                    <Icon className="h-4 w-4" />
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block text-xs font-black text-slate-900 group-hover/item:text-blue-700 transition-colors">
                                      {item.name}
                                    </span>
                                    <span className="block text-[10px] text-slate-500 line-clamp-1 leading-snug mt-0.5">
                                      {item.desc}
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* COLUMN 3: Featured Software Platforms (4 cols) */}
                        <div className="col-span-4 flex flex-col justify-between rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/70 via-indigo-50/30 to-slate-50 p-4">
                          <div>
                            <div className="flex items-center justify-between mb-3 pb-1 border-b border-blue-200/60">
                              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-blue-700">
                                <Zap className="h-3 w-3 text-amber-500 fill-amber-500" />
                                Flagship Software
                              </span>
                              <span className="text-[9px] font-bold text-amber-800 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded-full">
                                Instant Setup
                              </span>
                            </div>

                            <div className="space-y-2.5">
                              {/* G-Track 360 */}
                              <button
                                type="button"
                                onClick={() => handleNavClick({ id: '/contact', isPage: true })}
                                className="group/card w-full text-left rounded-xl bg-white p-3 border border-slate-200/80 shadow-xs hover:border-blue-500 hover:shadow-md transition-all block"
                              >
                                <div className="flex items-center justify-between">
                                  <h5 className="text-xs font-black text-slate-900 group-hover/card:text-blue-700 transition-colors">
                                    G-Track 360 HRM
                                  </h5>
                                  <span className="text-[9px] font-black text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                                    ₹8,499/yr
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                                  Biometric face &amp; GPS attendance, automated shifts &amp; 1-click payroll.
                                </p>
                              </button>

                              {/* G-Track POS */}
                              <button
                                type="button"
                                onClick={() => handleNavClick({ id: '/contact', isPage: true })}
                                className="group/card w-full text-left rounded-xl bg-white p-3 border border-slate-200/80 shadow-xs hover:border-blue-500 hover:shadow-md transition-all block"
                              >
                                <div className="flex items-center justify-between">
                                  <h5 className="text-xs font-black text-slate-900 group-hover/card:text-blue-700 transition-colors">
                                    G-Track POS Billing
                                  </h5>
                                  <span className="text-[9px] font-black text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                                    ₹9,999/yr
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                                  Hybrid online &amp; offline dual storage, GST barcode billing &amp; live mobile admin check.
                                </p>
                              </button>
                            </div>
                          </div>

                          {/* Bottom Action */}
                          <div className="mt-3 pt-2.5 border-t border-blue-200/60">
                            <button
                              type="button"
                              onClick={() => handleNavClick({ id: '/contact', isPage: true })}
                              className="inline-flex items-center justify-between w-full text-xs font-black text-blue-700 hover:text-blue-800 transition-colors"
                            >
                              <span>Request a Free Demonstration</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                )}

                {/* 2. CAREER DROPDOWN */}
                {isCareer && (
                  <div className="pointer-events-none absolute right-0 top-full w-80 pt-2.5 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 z-50">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
                      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-900">
                          Join Our Team
                        </span>
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-blue-700 border border-blue-200/80">
                          We're Hiring
                        </span>
                      </div>
                      <div className="space-y-1 p-1 mt-1">
                        {CAREER_ROLES.map((role) => {
                          const Icon = role.icon;
                          const iconBg = role.isTech
                            ? 'bg-blue-50 text-blue-700 border border-blue-200/80 group-hover/c:bg-blue-600 group-hover/c:text-white'
                            : 'bg-amber-50 text-amber-800 border border-amber-200/80 group-hover/c:bg-amber-500 group-hover/c:text-slate-950';
                          return (
                            <button
                              key={role.name}
                              type="button"
                              onClick={() => handleNavClick({ id: role.path, isPage: true })}
                              className="group/c flex items-start gap-3 w-full rounded-xl p-2.5 text-left transition hover:bg-slate-50"
                            >
                              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-xs transition-colors ${iconBg}`}>
                                <Icon className="h-4 w-4" />
                              </span>
                              <div>
                                <span className="block text-xs font-black text-slate-900 group-hover/c:text-blue-700 transition-colors">
                                  {role.name}
                                </span>
                                <span className="block text-[10px] text-slate-500 leading-snug mt-0.5">
                                  {role.desc}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* BOOK A DEMO CTA BUTTON */}
          <button
            type="button"
            onClick={handleBookDemo}
            className="group ml-2 relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-md shadow-blue-500/20 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-95"
          >
            <span>Book a Demo</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>

        {/* MOBILE MENU TOGGLE BUTTON */}
        <button
          type="button"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={() => setIsMenuOpen((open) => !open)}
          className={`flex h-10 w-10 items-center justify-center rounded-xl border lg:hidden ${
            isHomeTop
              ? 'border-white/20 bg-white/10 text-white'
              : 'border-slate-200 bg-white text-slate-900'
          }`}
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* MOBILE NAVIGATION DRAWER */}
      {isMenuOpen && (
        <div className="max-h-[85vh] overflow-y-auto border-t border-slate-200 bg-white px-5 py-4 shadow-2xl lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1.5">
            {navLinks.map((link) => {
              const active = isTabActive(link);
              const isServices = link.id === 'services' || link.name === 'Services';
              const isCareer = link.id === 'career' || link.name === 'Career';

              if (isServices) {
                return (
                  <div key={link.name} className="border-b border-slate-100 py-1">
                    <button
                      type="button"
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold ${
                        active ? 'bg-blue-50 text-blue-700 font-black' : 'text-slate-900'
                      }`}
                    >
                      <span>{link.name} (Mega Menu)</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          mobileServicesOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'
                        }`}
                      />
                    </button>

                    {mobileServicesOpen && (
                      <div className="space-y-4 pt-2 pb-3 pl-2">
                        {/* Core IT */}
                        <div>
                          <p className="px-2 text-[10px] font-black uppercase tracking-wider text-blue-700 mb-1.5">
                            Core IT Solutions
                          </p>
                          <div className="grid gap-1">
                            {CORE_IT_SERVICES.map((sub) => (
                              <button
                                type="button"
                                key={sub.name}
                                onClick={() => handleNavClick({ id: sub.path, isPage: true })}
                                className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-blue-700"
                              >
                                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-50 text-blue-700 border border-blue-200/80">
                                  <sub.icon className="h-3 w-3" />
                                </span>
                                <span>{sub.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Workforce */}
                        <div>
                          <p className="px-2 text-[10px] font-black uppercase tracking-wider text-amber-700 mb-1.5">
                            Workforce &amp; Consulting
                          </p>
                          <div className="grid gap-1">
                            {WORKFORCE_SERVICES.map((sub) => (
                              <button
                                type="button"
                                key={sub.name}
                                onClick={() => handleNavClick({ id: sub.path, isPage: true })}
                                className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-blue-700"
                              >
                                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-50 text-amber-800 border border-amber-200/80">
                                  <sub.icon className="h-3 w-3" />
                                </span>
                                <span>{sub.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Products */}
                        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                          <p className="text-[10px] font-black uppercase tracking-wider text-blue-700 mb-2">
                            Flagship Software
                          </p>
                          <button
                            type="button"
                            onClick={() => handleNavClick({ id: '/contact', isPage: true })}
                            className="w-full text-left text-xs font-bold text-slate-900 hover:text-blue-700 mb-1.5"
                          >
                            • G-Track 360 HRM Software (₹4,999/yr)
                          </button>
                          <button
                            type="button"
                            onClick={() => handleNavClick({ id: '/contact', isPage: true })}
                            className="w-full text-left text-xs font-bold text-slate-900 hover:text-blue-700"
                          >
                            • G-Track POS Billing Software (₹2,999/yr)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              if (isCareer) {
                return (
                  <div key={link.name} className="border-b border-slate-100 py-1">
                    <button
                      type="button"
                      onClick={() => setMobileCareerOpen(!mobileCareerOpen)}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold ${
                        active ? 'bg-blue-50 text-blue-700 font-black' : 'text-slate-900'
                      }`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          mobileCareerOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'
                        }`}
                      />
                    </button>
                    {mobileCareerOpen && (
                      <div className="grid gap-1 pt-1 pb-2 pl-3">
                        {CAREER_ROLES.map((role) => (
                          <button
                            type="button"
                            key={role.name}
                            onClick={() => handleNavClick({ id: role.path, isPage: true })}
                            className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-blue-700"
                          >
                            <span className={`flex h-6 w-6 items-center justify-center rounded-md ${
                              role.isTech
                                ? 'bg-blue-50 text-blue-700 border border-blue-200/80'
                                : 'bg-amber-50 text-amber-800 border border-amber-200/80'
                            }`}>
                              <role.icon className="h-3 w-3" />
                            </span>
                            <span>{role.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={link.name} className="border-b border-slate-100 py-1 last:border-0">
                  <button
                    type="button"
                    onClick={() => handleNavClick(link)}
                    className={`w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold transition ${
                      active ? 'bg-blue-50 text-blue-700 font-black' : 'text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {link.name}
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              onClick={handleBookDemo}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white shadow-md shadow-blue-500/25 transition active:scale-95"
            >
              <span>Book a Demo</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
